import React, { Component } from "react";
import { Alert, Modal, Select, Tag, message } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import {
  Form, FormItem, ModalItem, ButtonItem, DatePickerItem, InputNumberItem, SelectItem,
  TextAreaItem, CheckItem, ErrorItem, fmtFechaDia, claveFechaDia, fechaDiaParaPayload,
  dineroDisabledMask,
} from "../../../../Components/Items";
import { calcularSbcParteFija, MOTIVO_SBC_TEXTO } from "../sbc";

const { Option } = Select;

const ESTATUS_FINIQUITO_TEXTO = {
  aplicado: "aplicado",
  timbrado: "timbrado",
};

// Una fecha-día del backend (anclada al mediodía UTC) como el moment que espera el DatePicker.
const momentDeDia = (f) => {
  const clave = claveFechaDia(f);
  return clave ? moment(clave, "YYYY-MM-DD") : null;
};

/**
 * Reingreso de un empleado dado de baja: lo reactiva en el MISMO registro con una relación
 * laboral nueva (Controllers/Nomina/reingreso.controllers.js).
 *
 * Los candados viven en el backend (`evaluarReingreso`) y aquí sólo se pintan: la pantalla pide
 * `datosParaReingreso` al abrir y cada vez que cambian la fecha o el tipo de periodo, porque la
 * fecha mínima y las prenóminas que lo van a recibir dependen de los dos.
 *
 * Se envía con un botón `htmlType="button"` + validateFields, no con el submit del Form: el Form de
 * la casa ignora un segundo onFinish durante 5 s, y aquí lo normal es que el primer intento lo
 * rechace el backend (un recibo sin timbrar) y el usuario vuelva a intentar enseguida.
 */
class ModalReingreso extends Component {
  formRef = React.createRef();
  state = {
    loading: false,
    enviando: false,
    datos: null,
    sbcDetalle: null,
  };
  // Descarta las respuestas de una evaluación vieja: el usuario puede mover la fecha dos veces
  // antes de que regrese la primera.
  consultaVigente = 0;

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ datos: null, sbcDetalle: null });
      this.formRef.current?.resetFields();
      this.consultar({ inicial: true });
    }
  }

  async consultar({ inicial = false } = {}) {
    const valores = this.formRef.current?.getFieldsValue() || {};
    const consulta = ++this.consultaVigente;
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "nomina/datosParaReingreso",
        {
          empresaId: this.props.empresaId,
          empleadoId: this.props.empleadoId,
          fechaReingreso: inicial ? null : fechaDiaParaPayload(valores.fechaReingreso),
          tipoPeriodoId: inicial ? null : valores.tipoPeriodoId || null,
        },
        { headers: this.props.headersToken }
      );
      if (consulta !== this.consultaVigente) return;
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudo consultar al empleado");
        this.setState({ loading: false });
        return;
      }
      this.setState({ datos: res.data, loading: false });

      if (inicial) {
        const emp = res.data.empleado;
        const fechaInicial = momentDeDia(res.data.fechaPropuesta || res.data.fechaMinima);
        this.formRef.current?.setFieldsValue({
          fechaReingreso: fechaInicial,
          tipoPeriodoId: emp.tipoPeriodoId || undefined,
          salarioDiario: emp.salarioDiario ?? undefined,
          sbcParteFija: emp.sbcParteFija ?? undefined,
          avisoImss: true,
          nota: "",
        });
        // Con la fecha ya puesta: la segunda vuelta trae las prenóminas destino y el SBC propuesto
        // con la antigüedad de la relación NUEVA (que arranca en el primer año).
        if (fechaInicial) {
          this.proponerSbc(res.data);
          this.consultar();
        }
      }
    } catch (e) {
      if (consulta !== this.consultaVigente) return;
      this.setState({ loading: false });
      message.error("Error de conexión al consultar el reingreso");
    }
  }

  /** SBC con la misma fórmula que el F10 de Editar Empleado, medido desde el reingreso. */
  proponerSbc(datos = this.state.datos) {
    const emp = datos?.empleado;
    if (!emp) return;
    const { fechaReingreso, salarioDiario } = this.formRef.current?.getFieldsValue() || {};
    if (!fechaReingreso) return;
    const { sbc, anios, factor, motivo } = calcularSbcParteFija({
      empleado: {
        fechaAlta: emp.fechaAlta,
        fechaReingreso: fechaReingreso.toDate(),
        baseDeCotizacion: emp.baseDeCotizacion,
      },
      tipoPrestacion: datos.tipoPrestacion,
      salarioDiario,
      fechaReferencia: fechaReingreso.toDate(),
    });
    if (sbc) {
      this.formRef.current?.setFieldsValue({ sbcParteFija: sbc });
      this.setState({ sbcDetalle: { texto: `Antigüedad ${anios} año(s), factor ${factor}`, error: false } });
    } else {
      this.setState({
        sbcDetalle: { texto: MOTIVO_SBC_TEXTO[motivo] || "No se pudo calcular el SBC", error: true },
      });
    }
  }

  onValuesChange = (cambios) => {
    if ("fechaReingreso" in cambios || "salarioDiario" in cambios) this.proponerSbc();
    if ("fechaReingreso" in cambios || "tipoPeriodoId" in cambios) this.consultar();
  };

  cerrar(reingresado = false) {
    if (this.state.enviando) return;
    this.props.onCerrar(reingresado);
  }

  enviar = async () => {
    let valores;
    try {
      valores = await this.formRef.current.validateFields();
    } catch (e) {
      ErrorItem(e);
      return;
    }
    this.setState({ enviando: true });
    try {
      const res = await axios.post(
        "nomina/reingresarEmpleado",
        {
          empresaId: this.props.empresaId,
          empleadoId: this.props.empleadoId,
          fechaReingreso: fechaDiaParaPayload(valores.fechaReingreso),
          tipoPeriodoId: valores.tipoPeriodoId || null,
          salarioDiario: valores.salarioDiario ?? null,
          sbcParteFija: valores.sbcParteFija ?? null,
          avisoImss: !!valores.avisoImss,
          nota: valores.nota || "",
        },
        { headers: this.props.headersToken }
      );
      this.setState({ enviando: false });
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudo reingresar al empleado", 8);
        // Lo que cambió entre la carga y el clic (un recibo, un código ocupado) se vuelve a pintar.
        this.consultar();
        return;
      }
      message.success(res.data.mensaje || "Empleado reingresado");
      const { advertencias = [], prenominasAgregadas = [] } = res.data;
      if (advertencias.length || prenominasAgregadas.length) {
        Modal.info({
          title: "Reingreso aplicado",
          width: 560,
          content: (
            <div>
              {prenominasAgregadas.length > 0 && (
                <>
                  <p style={{ marginBottom: 4 }}>Quedó en estas prenóminas:</p>
                  <ul>
                    {prenominasAgregadas.map((p) => (
                      <li key={p._id}>
                        {p.etiqueta} ({p.estatus}{p.estatus === "calculada" ? ", ya recalculada" : ""})
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {advertencias.length > 0 && (
                <>
                  <p style={{ marginBottom: 4 }}>Revisa:</p>
                  <ul>{advertencias.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </>
              )}
            </div>
          ),
        });
      }
      this.props.onCerrar(true);
    } catch (e) {
      this.setState({ enviando: false });
      message.error("Error de conexión al reingresar");
    }
  };

  renderResumen() {
    const { datos } = this.state;
    const emp = datos?.empleado;
    if (!emp) return null;
    const fin = datos.finiquito;
    return (
      <div
        style={{
          background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 4,
          padding: "8px 12px", marginBottom: 12, fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {emp.nombre}
          {emp.codigo !== null && emp.codigo !== undefined && (
            <span style={{ color: "#888", fontWeight: 400 }}> · Código {String(emp.codigo).padStart(4, "0")}</span>
          )}
        </div>
        <div>
          Baja: <b>{fmtFechaDia(emp.fechaBaja)}</b>
          {emp.causaTexto ? ` — ${emp.causaTexto}` : ""}
          {emp.motivoBaja ? ` (${emp.motivoBaja})` : ""}
        </div>
        <div>
          {fin ? (
            <>
              Finiquito {ESTATUS_FINIQUITO_TEXTO[fin.estatus] || fin.estatus}, neto{" "}
              <b>{dineroDisabledMask(fin.totalNeto || 0)}</b>. Queda como historia de la relación anterior.
            </>
          ) : (
            "Baja sin finiquito. Queda registrada como historia de la relación anterior."
          )}
        </div>
      </div>
    );
  }

  renderPrenominas() {
    const { datos } = this.state;
    const destino = datos?.prenominasDestino || [];
    const bloqueadas = datos?.prenominasBloqueadas || [];
    if (!destino.length && !bloqueadas.length) return null;
    return (
      <div style={{ fontSize: 12, marginTop: 8 }}>
        {destino.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            Se va a agregar a:{" "}
            {destino.map((p) => (
              <Tag key={p._id} color={p.tieneLinea ? "default" : "blue"} style={{ marginBottom: 2 }}>
                {p.etiqueta} · {p.estatus}{p.tieneLinea ? " · ya está" : ""}
              </Tag>
            ))}
          </div>
        )}
        {bloqueadas.length > 0 && (
          <div style={{ color: "#ad6800" }}>
            No acepta empleados nuevos (hay que reabrirla):{" "}
            {bloqueadas.map((p) => (
              <Tag key={p._id} color="orange" style={{ marginBottom: 2 }}>{p.etiqueta} · {p.estatus}</Tag>
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { datos, loading, enviando, sbcDetalle } = this.state;
    const bloqueos = datos?.bloqueos || [];
    const avisos = datos?.avisos || [];
    const claveMinima = claveFechaDia(datos?.fechaMinima);

    return (
      <ModalItem
        title="Reingresar empleado"
        visible={this.props.visible}
        spinning={enviando}
        onCancel={() => this.cerrar(false)}
        paddingTop
        width={640}
        destroyOnClose
      >
        {this.renderResumen()}

        {bloqueos.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message="No se puede reingresar todavía"
            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{bloqueos.map((b, i) => <li key={i}>{b}</li>)}</ul>}
          />
        )}
        {avisos.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message="Antes de reingresar"
            description={<ul style={{ margin: 0, paddingLeft: 18 }}>{avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>}
          />
        )}

        <Form
          ref={this.formRef}
          name="reingresoEmpleado"
          layout
          onValuesChange={this.onValuesChange}
          bottom={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <ButtonItem htmlType="button" blanco onClick={() => this.cerrar(false)}>
                Cancelar
              </ButtonItem>
              <ButtonItem
                htmlType="button"
                type="primary"
                loading={enviando}
                disabled={loading || !datos || bloqueos.length > 0}
                onClick={this.enviar}
              >
                Reingresar
              </ButtonItem>
            </div>
          }
        >
          <FormItem
            name="fechaReingreso"
            label="Fecha de reingreso"
            required
            margin
            tooltip="Primer día de la relación laboral nueva. Es la que usan el CFDI, la antigüedad, el aguinaldo y las vacaciones de aquí en adelante."
            extra={claveMinima ? `A partir del ${fmtFechaDia(datos.fechaMinima)}` : undefined}
            rules={[
              {
                // DatePickerItem acepta fechas tecleadas sin pasar por disabledDate.
                validator: (_, v) => {
                  if (!v || !claveMinima || v.format("YYYY-MM-DD") >= claveMinima) return Promise.resolve();
                  return Promise.reject(new Error(`Tiene que ser a partir del ${fmtFechaDia(datos.fechaMinima)}`));
                },
              },
            ]}
          >
            <DatePickerItem
              placeholder="Fecha de reingreso"
              style={{ width: "100%" }}
              disabledDate={(d) => !!claveMinima && !!d && d.format("YYYY-MM-DD") < claveMinima}
            />
          </FormItem>
          <FormItem
            name="tipoPeriodoId"
            label="Tipo de periodo"
            margin
            tooltip="Con qué periodicidad se le va a pagar. Decide a qué prenóminas se agrega."
          >
            <SelectItem placeholder="Tipo de periodo">
              {(datos?.tiposPeriodo || []).map((t) => (
                <Option key={t._id} value={t._id}>{t.nombre}</Option>
              ))}
            </SelectItem>
          </FormItem>
          <FormItem name="salarioDiario" label="Salario diario" margin>
            <InputNumberItem placeholder="Salario diario" dinero />
          </FormItem>
          <FormItem
            label="SBC parte fija"
            margin
            extra={sbcDetalle ? (
              <span style={{ color: sbcDetalle.error ? "#cf1322" : "#888" }}>{sbcDetalle.texto}</span>
            ) : undefined}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <FormItem name="sbcParteFija" noStyle>
                <InputNumberItem placeholder="SBC" dinero style={{ flex: 1 }} />
              </FormItem>
              <ButtonItem htmlType="button" blanco size="small" onClick={() => this.proponerSbc()}>
                Calcular
              </ButtonItem>
            </div>
          </FormItem>
          <FormItem name="avisoImss" label="Aviso IMSS" valuePropName="checked" margin initialValue>
            <CheckItem>Marcar el aviso de reingreso al IMSS como pendiente</CheckItem>
          </FormItem>
          <FormItem name="nota" label="Nota" margin>
            <TextAreaItem placeholder="Motivo o comentario del reingreso (opcional)" rows={2} maxLength={500} />
          </FormItem>
        </Form>

        {this.renderPrenominas()}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalReingreso);
